import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileDownload,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileImage,
  faFile,
  faEye,
  faBook,
  faLayerGroup,
  faGraduationCap,
  faInfoCircle,
  faSearch,
  faFolderOpen,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../Services/ApiServices';
import FileViewer from '../Shared/FileViewer';
import Loader from '../Shared/Loader';
import Header from '../Shared/Header';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import Footer from '../Shared/Footer';
import toast from 'react-hot-toast';

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MozakaraStudent = () => {
  const [mozakaras, setMozakaras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [viewerFiles, setViewerFiles] = useState([]);
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMozakaraData();
  }, []);

  const fetchMozakaraData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiServices.get(
        `Teacher/Mozakara/StudentMozakara`
      );
      setMozakaras(response.data || []);
    } catch (err) {

      setError(err.msg || 'حدث خطأ في تحميل البيانات. يرجى المحاولة لاحقاً');
    } finally {
      setLoading(false);
    }
  };

  const getFileInfo = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const map = {
      pdf: { icon: faFilePdf, color: '#ef4444', label: 'PDF' },
      doc: { icon: faFileWord, color: '#3b82f6', label: 'DOC' },
      docx: { icon: faFileWord, color: '#3b82f6', label: 'DOCX' },
      xls: { icon: faFileExcel, color: '#10b981', label: 'XLS' },
      xlsx: { icon: faFileExcel, color: '#10b981', label: 'XLSX' },
      ppt: { icon: faFilePowerpoint, color: '#6366f1', label: 'PPT' },
      pptx: { icon: faFilePowerpoint, color: '#6366f1', label: 'PPTX' },
      jpg: { icon: faFileImage, color: '#ec4899', label: 'JPG' },
      jpeg: { icon: faFileImage, color: '#ec4899', label: 'JPEG' },
      png: { icon: faFileImage, color: '#ec4899', label: 'PNG' },
    };
    return map[ext] || { icon: faFile, color: '#64748b', label: ext.toUpperCase() };
  };

  const handleDownload = (attachment) => {
    const fullUrl = getFullFileUrl(attachment.file_name);
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = attachment.file_name.split(/[\\/]/).pop() || 'file';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('بدء تحميل الملف...');
  };

  const handleViewFiles = (attachments, index = 0) => {
    setViewerFiles(attachments);
    setSelectedFileIndex(index);
    setFileViewerOpen(true);
  };

  const filteredMozakaras = (mozakaras || []).filter(m =>
    (m?.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m?.material || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <Loader text="جاري تحميل المذكرات..." />;
  }

  return (
    <div className="mz-page">
      <Header />
      <br />

      <FileViewer
        isOpen={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        files={viewerFiles}
        currentIndex={selectedFileIndex}
      />

      <section className="mz-root" id="mozakara-section">
        <div className="mz-container">
          {/* ── header ── */}
          <motion.div
            className="mz-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="mz-eyebrow" variants={fadeUp} custom={0}>
              <span className="mz-eyebrow-dot" />
              مكتبة المذكرات الرقمية
            </motion.div>
            <motion.h2 className="mz-title" variants={fadeUp} custom={1}>
              استكشف <em>مذكراتك الدراسية</em>
            </motion.h2>
            <motion.p className="mz-subtitle" variants={fadeUp} custom={2}>
              كل ما تحتاجه من ملخصات ومذكرات مجمعة في مكان واحد لتسهيل رحلتك التعليمية
            </motion.p>
          </motion.div>

          <div className="mz-search-bar">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="ابحث عن مذكرة أو مادة معينة..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <AnimatePresence mode="wait">
            {error ? (
              <motion.div key="error" className="mz-empty-state" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="icon-wrap"><FontAwesomeIcon icon={faInfoCircle} /></div>
                <h3>{error}</h3>
                <button onClick={fetchMozakaraData}>إعادة المحاولة</button>
              </motion.div>
            ) : filteredMozakaras.length === 0 ? (
              <motion.div key="empty" className="mz-empty-state" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="icon-wrap"><FontAwesomeIcon icon={faFolderOpen} /></div>
                <h3>لا توجد مذكرات مطابقة لبحثك</h3>
                <p>جرب كلمات بحث أخرى أو تأكد من اختيار القسم الصحيح</p>
              </motion.div>
            ) : (
              <div className="mz-list-grid">
                {filteredMozakaras.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    className="mz-item-card"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={idx}
                  >
                    <div className="mz-card-head">
                      <div className="mz-material-badge">
                        <FontAwesomeIcon icon={faBook} />
                        <span>{item.material}</span>
                      </div>
                      <div className="mz-class-badge">
                        <FontAwesomeIcon icon={faGraduationCap} />
                        <span>{item.user_class_name}</span>
                      </div>
                    </div>

                    <h3 className="mz-item-title">{item.title}</h3>
                    <p className="mz-item-desc">{item.description}</p>

                    <div className="mz-attachments-section">
                      <div className="sec-label">
                        <FontAwesomeIcon icon={faLayerGroup} />
                        <span>الملفات المرفقة ({item.attachments?.length || 0})</span>
                      </div>
                      <div className="mz-files-list">
                        {item.attachments?.map((file, fIdx) => {
                          const info = getFileInfo(file.file_name);
                          const shortName = file.file_name.split(/[\\/]/).pop();
                          return (
                            <div key={file.id} className="mz-file-pill">
                              <div className="file-info" onClick={() => handleViewFiles(item.attachments, fIdx)}>
                                <FontAwesomeIcon icon={info.icon} style={{ color: info.color }} />
                                <span title={shortName}>{shortName}</span>
                              </div>
                              <button className="dl-btn" onClick={() => handleDownload(file)}>
                                <FontAwesomeIcon icon={faFileDownload} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mz-card-footer">
                      <div className="date">
                        <FontAwesomeIcon icon={faClock} />
                        <span>{new Date(item.created_date).toLocaleDateString('ar-EG') === '١/١/٠٠٠١' ? 'مضافة حديثاً' : new Date(item.created_date).toLocaleDateString('ar-EG')}</span>
                      </div>
                      <button className="mz-btn-view-all" onClick={() => handleViewFiles(item.attachments, 0)}>
                        <FontAwesomeIcon icon={faEye} />
                        عرض الكل
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </AnimatePresence>
        </div>
        <Footer/>
      </section>

      <style>{`
        .mz-page { background: #fff; min-height: 100vh; }
        .mz-root {
          font-family: "Zain", sans-serif; direction: rtl;
          padding: 80px 0 100px; isolation: isolate;
        }
        .mz-container { max-width: 1260px; margin: 0 auto; padding: 0 40px; }

        /* Header */
        .mz-header { text-align: center; margin-bottom: 40px; }
        .mz-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.82rem;
          letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
        }
        .mz-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: mz-pulse 2.2s ease-in-out infinite;
        }
        @keyframes mz-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
          50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
        }
        .mz-title { font-size: clamp(2.2rem, 4vw, 3.6rem); font-weight: 900; color: #0f172a; margin: 0 0 18px; line-height: 1.2; }
        .mz-title em { font-style: normal; background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .mz-subtitle { font-size: 1.08rem; color: #64748b; line-height: 1.85; max-width: 700px; margin: 0 auto; }

        .mz-search-bar {
          max-width: 600px; margin: 0 auto 50px; position: relative; padding: 0;
        }
        .mz-search-bar .search-icon { position: absolute; right: 25px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 1.1rem; }
        .mz-search-bar input {
          width: 100%; padding: 18px 60px 18px 25px; border-radius: 20px;
          border: 1.5px solid #f1f5f9; background: #f8fafc; font-family: inherit;
          font-weight: 800; font-size: 1.05rem; color: #0f172a; outline: none; transition: 0.3s;
          box-sizing: border-box;
        }
        .mz-search-bar input:focus { border-color: #6366f1; background: #fff; box-shadow: 0 10px 30px rgba(99,102,241,0.06); }

        /* Grid */
        .mz-list-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(380px, 100%), 1fr)); gap: 30px; }

        /* Card */
        .mz-item-card {
          background: #fff; border: 1px solid #f1f5f9; border-radius: 32px; padding: 35px;
          display: flex; flex-direction: column; transition: 0.4s cubic-bezier(0.22, 1, 0.36, 1);
          box-shadow: 0 10px 30px rgba(0,0,0,0.02); min-width: 0; overflow: hidden;
        }
        .mz-item-card:hover { transform: translateY(-8px); border-color: #e2e8f0; box-shadow: 0 20px 40px rgba(0,0,0,0.04); }

        .mz-card-head { display: flex; gap: 12px; margin-bottom: 25px; }
        .mz-material-badge, .mz-class-badge {
          display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 10px;
          font-weight: 900; font-size: 0.8rem;
        }
        .mz-material-badge { background: #f0f9ff; color: #0ea5e9; }
        .mz-class-badge { background: #fdf2f8; color: #db2777; }

        .mz-item-title { font-size: 1.5rem; font-weight: 900; color: #0f172a; margin: 0 0 12px; line-height: 1.3; }
        .mz-item-desc { font-size: 1rem; color: #64748b; font-weight: 700; line-height: 1.7; margin: 0 0 25px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

        .mz-attachments-section { flex: 1; background: #f8fafc; border-radius: 20px; padding: 20px; margin-bottom: 25px; }
        .sec-label { display: flex; align-items: center; gap: 10px; font-weight: 900; color: #475569; font-size: 0.9rem; margin-bottom: 15px; }
        .sec-label i { color: #6366f1; }
        .mz-files-list { display: flex; flex-direction: column; gap: 10px; }
        .mz-file-pill {
          background: #fff; border: 1px solid #f1f5f9; padding: 8px 12px; border-radius: 12px;
          display: flex; align-items: center; justify-content: space-between; gap: 10px;
        }
        .mz-file-pill .file-info { display: flex; align-items: center; gap: 12px; flex: 1; cursor: pointer; overflow: hidden; min-width: 0; }
        .mz-file-pill .file-info span { font-weight: 800; color: #334155; font-size: 0.85rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mz-file-pill .dl-btn { background: #f1f5f9; color: #64748b; border: none; width: 32px; height: 32px; border-radius: 8px; cursor: pointer; transition: 0.2s; flex-shrink: 0; }
        .mz-file-pill .dl-btn:hover { background: #10b981; color: #fff; }

        .mz-card-footer { display: flex; align-items: center; justify-content: space-between; border-top: 1px dashed #f1f5f9; pt: 20px; padding-top: 20px; }
        .mz-card-footer .date { display: flex; align-items: center; gap: 8px; color: #94a3b8; font-weight: 700; font-size: 0.85rem; }
        .mz-btn-view-all {
          background: #6366f1; color: #fff; border: none; padding: 8px 20px; border-radius: 12px;
          font-family: inherit; font-weight: 900; font-size: 0.9rem; cursor: pointer;
          display: flex; align-items: center; gap: 10px; transition: 0.3s;
        }
        .mz-btn-view-all:hover { background: #4f46e5; box-shadow: 0 10px 20px rgba(99,102,241,0.2); }

        .mz-empty-state { text-align: center; padding: 100px 20px; background: #f8fafc; border-radius: 40px; }
        .mz-empty-state .icon-wrap { font-size: 4rem; color: #cbd5e1; margin-bottom: 25px; }
        .mz-empty-state h3 { font-size: 1.5rem; font-weight: 900; color: #64748b; margin-bottom: 10px; }
        .mz-empty-state p { color: #94a3b8; font-weight: 700; }
        .mz-empty-state button { margin-top: 25px; background: #6366f1; color: #fff; border: none; padding: 12px 30px; border-radius: 15px; font-weight: 900; cursor: pointer; }

        /* ── Responsive ── */
        @media(max-width:1100px){
          .mz-list-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media(max-width:768px){
          .mz-root { padding: 60px 0 80px; }
          .mz-container { padding: 0 16px; }
          .mz-title { font-size: clamp(1.8rem, 6vw, 2.6rem); }
          .mz-subtitle { font-size: 0.98rem; }
          .mz-list-grid { grid-template-columns: 1fr; gap: 20px; }
          .mz-item-card { padding: 22px; border-radius: 24px; }
          .mz-card-head { flex-wrap: wrap; gap: 8px; margin-bottom: 18px; }
          .mz-material-badge, .mz-class-badge { font-size: 0.75rem; padding: 5px 12px; }
          .mz-item-title { font-size: 1.2rem; }
          .mz-item-desc { font-size: 0.95rem; }
          .mz-attachments-section { padding: 15px; }
          .mz-file-pill { padding: 8px 10px; }
          .mz-file-pill .file-info span { font-size: 0.78rem; }
          .mz-card-footer { flex-direction: row; flex-wrap: wrap; gap: 12px; }
          .mz-btn-view-all { flex: 1; justify-content: center; }
          .mz-search-bar { margin-bottom: 30px; }
          .mz-search-bar input { padding: 14px 50px 14px 18px; font-size: 0.95rem; }
          .mz-empty-state { padding: 60px 16px; border-radius: 24px; }
          .mz-empty-state h3 { font-size: 1.2rem; }
        }

        @media(max-width:480px){
          .mz-item-card { padding: 18px; border-radius: 20px; }
          .mz-item-title { font-size: 1.1rem; }
          .mz-card-footer { flex-direction: column; align-items: stretch; }
          .mz-btn-view-all { justify-content: center; width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default MozakaraStudent;
